'use client';

import { StatusHero } from '@/components/StatusHero';
import {
  ArrowRightIcon,
  BookOpenIcon,
  CalendarIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import Link from 'next/link';
import { useMemo, useState } from 'react';
import { BlogPost } from '../../data/blogPosts';

interface BlogClientComponentProps {
  allBlogPosts: BlogPost[];
}

export default function BlogClientComponent({ allBlogPosts }: BlogClientComponentProps) {
  const [selectedCategory, setSelectedCategory] = useState('All Posts');

  // Get unique categories from blog posts
  const categories = useMemo(() => {
    const uniqueCategories = [...new Set(allBlogPosts.map(post => post.category))];
    return ['All Posts', ...uniqueCategories];
  }, [allBlogPosts]);

  // Filter blog posts based on selected category
  const filteredBlogPosts = useMemo(() => {
    if (selectedCategory === 'All Posts') {
      return allBlogPosts;
    }
    return allBlogPosts.filter(post => post.category === selectedCategory);
  }, [allBlogPosts, selectedCategory]);

  // Get featured post (first post from filtered results)
  const featuredPost = filteredBlogPosts.length > 0 ? filteredBlogPosts[0] : null;

  // Get remaining posts for the grid (excluding featured)
  const gridPosts = filteredBlogPosts.slice(1);

  return (
    <>
      {/* Header */}
      <StatusHero
        icon={BookOpenIcon}
        title="Greet Blog"
        description="Insights, tips, and stories about AI phone receptionists for small businesses"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Category Filter */}
        <div className="mb-12">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  category === selectedCategory
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Featured Post */}
        {featuredPost && (
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Featured Article</h2>
            <Link
              href={`/blog/${featuredPost.slug}`}
              className="block bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow group"
            >
              <div className="md:flex md:max-h-80">
                <div className="md:w-1/3 shrink-0">
                  <img
                    src={featuredPost.featuredImage}
                    alt={featuredPost.title}
                    className="w-full h-64 md:h-full object-cover"
                  />
                </div>
                <div className="md:w-2/3 p-8 overflow-hidden">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {featuredPost.category}
                    </span>
                    <div className="flex items-center text-sm text-gray-500">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      {new Date(featuredPost.publishDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      {featuredPost.readTime}
                    </div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors">
                    {featuredPost.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {featuredPost.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <img
                        src={featuredPost.author.avatar}
                        alt={featuredPost.author.name}
                        className="h-10 w-auto mr-3 bg-white p-1 object-contain"
                      />
                      <div>
                        <div className="font-medium text-gray-900">{featuredPost.author.name}</div>
                        <div className="text-sm text-gray-500">{featuredPost.author.role}</div>
                      </div>
                    </div>
                    
                    <div className="inline-flex items-center text-blue-600 group-hover:text-blue-700 font-medium">
                      Read Article
                      <ArrowRightIcon className="h-4 w-4 ml-2" />
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        )}

        {/* All Posts Grid */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-8">
            {selectedCategory === 'All Posts' ? 'All Articles' : `${selectedCategory} Articles`}
          </h2>
          {gridPosts.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {gridPosts.map((post) => (
                <Link
                  key={post.id}
                  href={`/blog/${post.slug}`}
                  className="block bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow group"
                >
                  <article>
                    <img
                      src={post.featuredImage}
                      alt={post.title}
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {post.category}
                        </span>
                        <div className="flex items-center text-xs text-gray-500">
                          <ClockIcon className="h-3 w-3 mr-1" />
                          {post.readTime}
                        </div>
                      </div>
                      
                      <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors">
                        {post.title}
                      </h3>
                      
                      <p className="text-gray-600 mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                      
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-500">
                          {new Date(post.publishDate).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                        
                        <div className="inline-flex items-center text-blue-600 group-hover:text-blue-700 font-medium text-sm">
                          Read More
                          <ArrowRightIcon className="h-3 w-3 ml-1" />
                        </div>
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No articles found in this category.</p>
            </div>
          )}
        </div>

        {/* Newsletter Signup */}
        <div className="mt-16 bg-blue-600 rounded-xl p-8 text-center">
          <h3 className="text-2xl font-bold text-white mb-4">
            Stay Updated with Greet
          </h3>
          <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
            Get the latest insights on Greet, small business tips, and customer success stories delivered to your inbox.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-lg border-0 focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600"
            />
            <button className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors">
              Subscribe
            </button>
          </div>
        </div>
      </div>
    </>
  );
} 